import { RefObject, useState } from "react";
import { MODAL_TYPE } from "../../../constants";
import { useViewport } from "../../../context/viewport/ViewportContext";
import Modal from "../../modal/Modal";
import Button from "../../buttons/button/Button";
import { BUTTON_TYPES } from "../../buttons/button/ButtonTypes";
import {
  ProgressIndicator,
  SIZE_SMALL,
} from "@asosteam/asos-web-component-library-progress-indicator";
import AnonymousBackInStockRecognisedCustomer from "../AnonymousBackInStockRecognisedCustomer";
import AnonymousBackInStockNotRecognisedCustomer from "../AnonymousBackInStockNotRecognisedCustomer";
import styles from "../AnonymousBackInStockModal.scss";
import replaceJSX from "../utils/replaceJSX";
import { VariantConfig } from "./variantConfig";
import { fireEnterEmailClickAnalytics } from "../../../analytics/anonymousBackInStock";
import classNames from "classnames";

export type VariantModalProps = {
  config: VariantConfig;
  closeModal: () => void;
  focusOnCloseElement?: HTMLElement | null;
  emailIsRegistered: boolean;
  emailInputRef: RefObject<HTMLInputElement>;
  email: string;
  emailError: boolean;
  loading: boolean;
  setEmail: (value: string) => void;
  setEmailError: (value: boolean) => void;
  handleNotifyClick: () => void;
  setEmailIsRegistered: (value: boolean) => void;
  backInStockSignIn: (value?: string) => void;
  getTranslation: (key: string) => string;
  recognisedHeadingRef: RefObject<HTMLHeadingElement>;
  selectedVariantId?: number;
};

const AnonymousBackInStockVariantModal = ({
  config,
  closeModal,
  focusOnCloseElement,
  emailIsRegistered,
  emailInputRef,
  email,
  emailError,
  loading,
  setEmail,
  setEmailError,
  handleNotifyClick,
  setEmailIsRegistered,
  backInStockSignIn,
  getTranslation,
  recognisedHeadingRef,
  selectedVariantId,
}: VariantModalProps): JSX.Element => {
  const { viewportCategory } = useViewport()! as { viewportCategory: string };
  const [emailFormVisible, setEmailFormVisible] = useState(!config.twoStep);

  const handleEnterEmailClick = () => {
    fireEnterEmailClickAnalytics(selectedVariantId);
    setEmailFormVisible(true);
  };

  const modalType =
    viewportCategory === "small-viewport"
      ? config.mobileModalType
      : MODAL_TYPE.CENTER;

  const headingCopy = getTranslation(config.headingKey);
  const bodyCopy = config.bodyKeys.map((key) => getTranslation(key)).join(". ");

  const policyBlock = (
    <div className={styles.consentWarning} data-testid="anon-bis-policy">
      {replaceJSX(
        getTranslation("anonymous_back_in_stock_modal_terms_link"),
        ["%{privacypolicylink}", "%{termslink}"],
        [
          <a
            key="privacypolicylink"
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getTranslation("anonymous_back_in_stock_modal_policy_link_text")}
          </a>,
          <a
            key="termslink"
            href="/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getTranslation("anonymous_back_in_stock_modal_terms_link_text")}
          </a>,
        ],
      )}
    </div>
  );

  const renderContent = () => {
    if (config.onEmailRegistered === "showGuard" && emailIsRegistered) {
      return (
        <AnonymousBackInStockRecognisedCustomer
          recognisedHeadingRef={recognisedHeadingRef}
          selectedVariantId={selectedVariantId}
          onSignIn={() => backInStockSignIn()}
          onUseDifferentEmail={() => setEmailIsRegistered(false)}
        />
      );
    }

    if (config.twoStep && !emailFormVisible) {
      const enterEmailCta = getTranslation(
        "anonymous_back_in_stock_modal_enter_email_cta",
      );
      return (
        <>
          <h2 id="modalTitle" className={styles.title}>
            {headingCopy}
          </h2>
          <p
            className={classNames(styles.intro, styles.introWithBottomPadding)}
          >
            {bodyCopy}
          </p>
          <Button
            handleClick={() =>
              backInStockSignIn("anonymous notify me screen - sign in button")
            }
            type={BUTTON_TYPES.primary}
            additionalClasses={styles.button}
          >
            {getTranslation("back_in_stock_sign_in_modal_button")}
          </Button>
          <div className={styles.inputWrapper}>
            <p className={classNames(styles.intro, styles.introSubLabel)}>
              {getTranslation("anonymous_back_in_stock_modal_secondary_body")}
            </p>
          </div>
          {emailError && (
            <p
              id="anon-bis-email-error"
              className={styles.emailValidationError}
              role="alert"
            >
              {getTranslation("anonymous_back_in_stock_modal_email_error")}
            </p>
          )}
          <Button
            handleClick={handleEnterEmailClick}
            type={BUTTON_TYPES.secondary}
            additionalClasses={classNames(
              styles.button,
              styles.buttonSecondary,
            )}
            disabled={loading}
            ariaLabel={enterEmailCta}
          >
            {loading ? (
              <ProgressIndicator size={SIZE_SMALL} aria-hidden="true" />
            ) : (
              enterEmailCta
            )}
          </Button>
          {policyBlock}
        </>
      );
    }

    const showSignInFirst =
      config.showSignInFirst && !(config.twoStep && emailFormVisible);
    const notifyButtonType =
      config.twoStep && emailFormVisible
        ? BUTTON_TYPES.primary
        : config.notifyButtonType;

    return (
      <>
        {showSignInFirst && (
          <>
            <h2 id="modalTitle" className={styles.title}>
              {headingCopy}
            </h2>
            <p
              className={classNames(
                styles.intro,
                styles.introWithBottomPadding,
              )}
            >
              {bodyCopy}
            </p>
            <Button
              handleClick={() =>
                backInStockSignIn("anonymous notify me screen - sign in button")
              }
              type={BUTTON_TYPES.primary}
              additionalClasses={styles.button}
            >
              {getTranslation("back_in_stock_sign_in_modal_button")}
            </Button>
          </>
        )}
        <AnonymousBackInStockNotRecognisedCustomer
          title={showSignInFirst ? "" : headingCopy}
          body={showSignInFirst ? "" : bodyCopy}
          subLabel={
            showSignInFirst
              ? getTranslation("anonymous_back_in_stock_modal_secondary_body")
              : undefined
          }
          buttonType={notifyButtonType}
          emailInputRef={emailInputRef}
          email={email}
          emailError={emailError}
          loading={loading}
          setEmail={setEmail}
          setEmailError={setEmailError}
          handleNotifyClick={handleNotifyClick}
          getTranslation={getTranslation}
          selectedVariantId={selectedVariantId}
          twoStep={config.twoStep}
        />
      </>
    );
  };

  return (
    <Modal
      contentLoaded={true}
      modalType={modalType}
      open
      closeModal={closeModal}
      modalId="AnonymousBackInStockModal"
      forceDOMRewrite={true}
      withScrollBar={false}
      focusOnClose={focusOnCloseElement}
    >
      <div className={styles.container}>{renderContent()}</div>
    </Modal>
  );
};

export default AnonymousBackInStockVariantModal;
